/**
 * US-Eastern time helpers for the Combo caption — ported from `combo_time.dart`.
 * No timezone library: just the fixed US DST rule so the displayed time is
 * correct year-round (EST = UTC−5, EDT = UTC−4).
 *
 * DST runs from 02:00 local on the 2nd Sunday of March to 02:00 local on the
 * 1st Sunday of November. We compare against the transition instants in UTC
 * (07:00Z spring-forward, 06:00Z fall-back).
 */

/** The UTC instant of the n-th Sunday of `month` (0-based) in `year`, at `utcHour`. */
function nthSundayUtc(year: number, month: number, n: number, utcHour: number): Date {
  const first = new Date(Date.UTC(year, month, 1));
  // getUTCDay(): Sun=0 .. Sat=6. Days to the first Sunday, then +weeks.
  const toFirstSunday = (7 - first.getUTCDay()) % 7;
  const day = 1 + toFirstSunday + (n - 1) * 7;
  return new Date(Date.UTC(year, month, day, utcHour));
}

/** Whether `utc` falls inside US-Eastern daylight saving (EDT). */
function isEasternDst(utc: Date): boolean {
  const y = utc.getUTCFullYear();
  const start = nthSundayUtc(y, 2 /* March */, 2, 7); // 02:00 EST = 07:00Z
  const end = nthSundayUtc(y, 10 /* November */, 1, 6); // 02:00 EDT = 06:00Z
  return utc.getTime() >= start.getTime() && utc.getTime() < end.getTime();
}

/**
 * `src` expressed as US-Eastern wall-clock. The returned `Date`'s UTC field
 * accessors (`getUTCMonth()`/`getUTCHours()`/…) read as Eastern local time;
 * treat it as a display value only.
 */
export function comboToEastern(src: Date): Date {
  const offsetH = isEasternDst(src) ? 4 : 5;
  return new Date(src.getTime() - offsetH * 3600 * 1000);
}

/** The Eastern zone abbreviation for `src` — "EDT" in summer, "EST" in winter. */
export function comboEasternZone(src: Date): string {
  return isEasternDst(src) ? 'EDT' : 'EST';
}

/** Parse an ISO string to a UTC `Date`, appending `Z` when no zone is present. */
export function parseUtc(t: string): Date {
  const hasZone = t.endsWith('Z') || /[+-]\d\d:?\d\d$/.test(t);
  return new Date(hasZone ? t : `${t}Z`);
}
